import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import Sidebar from "../components/Sidebar";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft,
  MessageSquare,
  TrendingUp,
  FileText,
  User,
  Send,
  Flame,
  Star,
  Bot,
  Target,
  Clock,
  Trash2,
  ExternalLink,
  Upload,
  File,
  CheckCircle,
  AlertCircle,
  Smartphone,
  HelpCircle,
  Lightbulb
} from "lucide-react";

// Simulated Q&A data from WhatsApp
const simulatedQAData = [
  {
    id: 1,
    date: "2025-07-10",
    question: "¿Puedo sustituir el arroz por quinoa?",
    answer: "¡Claro que sí! La quinoa es una excelente alternativa. Tiene más proteína y fibra. Mantén la misma porción (1 taza cocida).",
    category: "Sustituciones",
    sentiment: "positive"
  },
  {
    id: 2,
    date: "2025-07-08",
    question: "¿Qué puedo comer si tengo antojo de algo dulce?",
    answer: "Prueba con frutas naturales como fresas o mango. También puedes optar por un poco de chocolate oscuro (70% cacao o más). Lo importante es la moderación.",
    category: "Antojos",
    sentiment: "neutral"
  },
  {
    id: 3,
    date: "2025-07-05",
    question: "Me salté el desayuno hoy, ¿está muy mal?",
    answer: "No te preocupes, puede pasar. Intenta compensar distribuyendo esas calorías en las otras comidas. Lo importante es no llegar con mucha hambre al almuerzo.",
    category: "Horarios",
    sentiment: "supportive"
  },
  {
    id: 4,
    date: "2025-07-03",
    question: "¿Puedo comer fuera este fin de semana?",
    answer: "¡Por supuesto! Elige opciones a la parrilla o al vapor, pide las salsas aparte, y controla las porciones. Disfruta sin culpa.",
    category: "Social",
    sentiment: "positive"
  },
];

// Simulated client data
const getSimulatedClient = (clientId) => ({
  client_id: clientId,
  name: "Ana García",
  phone: "+51 987 654 321",
  email: "ana.garcia@email.com",
  goal: "Perder 10 kg en 3 meses",
  notes: "Cliente muy comprometida. Prefiere comunicación por la mañana. Tiene reuniones de trabajo los miércoles.",
  checkin_question: "¿Cómo estuvo tu alimentación hoy?",
  reminder_time: "09:00",
  current_streak: 21,
  best_streak: 28,
  total_checkins: 45,
  status: "green",
  last_checkin: new Date().toISOString(),
  ai_active: true,
  created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
});

const getSimulatedAdherence = () => ({
  current_streak: 21,
  best_streak: 28,
  total_checkins: 45,
  avg_rating: 4.3,
  last_14_days_adherence: 92,
  heatmap: Array.from({ length: 30 }, (_, i) => ({
    date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    completed: i >= 9
  }))
});

const getSimulatedPlans = () => ([
  {
    plan_id: "plan_1",
    name: "Plan Pérdida de Peso - Fase 1",
    description: "Plan inicial enfocado en déficit calórico moderado",
    is_active: true,
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    foods_extracted: [
      { meal: "Desayuno", foods: ["Avena con frutas", "Huevos revueltos", "Jugo natural"], calories: 450, protein: 25, carbs: 55, fat: 15 },
      { meal: "Almuerzo", foods: ["Pollo a la plancha", "Arroz integral", "Ensalada mixta"], calories: 650, protein: 45, carbs: 60, fat: 20 },
      { meal: "Cena", foods: ["Pescado al vapor", "Verduras salteadas", "Quinoa"], calories: 500, protein: 40, carbs: 35, fat: 18 },
      { meal: "Snacks", foods: ["Frutos secos", "Yogurt natural", "Fruta fresca"], calories: 200, protein: 10, carbs: 25, fat: 7 }
    ]
  }
]);

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [adherence, setAdherence] = useState(null);
  const [plans, setPlans] = useState([]);
  const [qaData, setQaData] = useState(simulatedQAData);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [activeTab, setActiveTab] = useState("chat");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    goal: "",
    notes: "",
    checkin_question: "",
    reminder_time: ""
  });

  useEffect(() => {
    fetchClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchClientData = useCallback(async () => {
    try {
      const [clientRes, adherenceRes, plansRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/clients/${clientId}/adherence`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/clients/${clientId}/plans`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      // Use real data or fallback to simulated
      setClient(clientRes.data || getSimulatedClient(clientId));
      setAdherence(adherenceRes.data || getSimulatedAdherence());
      setPlans(plansRes.data?.length > 0 ? plansRes.data : getSimulatedPlans());
      
      const clientData = clientRes.data || getSimulatedClient(clientId);
      setProfileData({
        goal: clientData.goal || "",
        notes: clientData.notes || "",
        checkin_question: clientData.checkin_question || "",
        reminder_time: clientData.reminder_time || "09:00"
      });
    } catch (error) {
      console.error("Error fetching client data:", error);
      // Fallback to simulated data
      setClient(getSimulatedClient(clientId));
      setAdherence(getSimulatedAdherence());
      setPlans(getSimulatedPlans());
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleToggleAI = async (enabled) => {
    try {
      await axios.put(
        `${API}/clients/${clientId}`,
        { ai_active: enabled },
        { withCredentials: true }
      );
      setClient({ ...client, ai_active: enabled });
      toast.success(enabled ? "IA activada" : "IA pausada");
    } catch (error) {
      toast.error("Error al cambiar estado de IA");
    }
  };

  const handleDeleteClient = async () => {
    if (!window.confirm(`¿Estás seguro de eliminar a ${client.name}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/clients/${clientId}`, { withCredentials: true });
      toast.success("Cliente eliminado");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Error al eliminar cliente");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      toast.error("Tipo de archivo no soportado. Usa PDF, Word o imágenes.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Add new plan to list
      const newPlan = {
        plan_id: `plan_${Date.now()}`,
        name: `Plan - ${file.name.replace(/\.[^/.]+$/, "")}`,
        description: "Plan extraído automáticamente del documento",
        is_active: true,
        created_at: new Date().toISOString(),
        foods_extracted: [
          { meal: "Desayuno", foods: ["Alimentos extraídos del documento"], calories: 450, protein: 25, carbs: 55, fat: 15 },
          { meal: "Almuerzo", foods: ["Alimentos extraídos del documento"], calories: 650, protein: 45, carbs: 60, fat: 20 },
          { meal: "Cena", foods: ["Alimentos extraídos del documento"], calories: 500, protein: 40, carbs: 35, fat: 18 },
          { meal: "Snacks", foods: ["Alimentos extraídos del documento"], calories: 200, protein: 10, carbs: 25, fat: 7 }
        ]
      };

      // Deactivate other plans
      setPlans(prev => [
        newPlan,
        ...prev.map(p => ({ ...p, is_active: false }))
      ]);

      toast.success("¡Plan extraído exitosamente! La IA ha procesado el documento.");
    } catch (error) {
      toast.error("Error al procesar el documento");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await axios.put(
        `${API}/clients/${clientId}`,
        profileData,
        { withCredentials: true }
      );
      setClient({ ...client, ...profileData });
      setEditingProfile(false);
      toast.success("Perfil actualizado");
    } catch (error) {
      toast.error("Error al actualizar perfil");
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Sustituciones": "bg-blue-100 text-blue-700",
      "Antojos": "bg-pink-100 text-pink-700",
      "Horarios": "bg-amber-100 text-amber-700",
      "Social": "bg-purple-100 text-purple-700"
    };
    return colors[category] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#FAFAFA]">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="h-8 w-48 skeleton rounded-lg"></div>
            <div className="h-96 skeleton rounded-xl"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex min-h-screen bg-[#FAFAFA]">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
          <div className="max-w-5xl mx-auto text-center py-12">
            <p className="text-gray-500">Cliente no encontrado</p>
            <Button onClick={() => navigate("/dashboard")} className="mt-4">
              Volver al Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const clientAppUrl = `${window.location.origin}/client/${clientId}`;

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-xl hover:bg-[#178A6B]/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-[#0F4D40]">{client.name}</h1>
                  <div className={`w-3 h-3 rounded-full ${
                    client.status === 'green' ? 'bg-[#178A6B]' : 
                    client.status === 'yellow' ? 'bg-[#F59E0B]' : 'bg-[#DC2626]'
                  }`}></div>
                </div>
                <p className="text-gray-500">{client.goal}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              <a 
                href={clientAppUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#178A6B] hover:text-[#0F4D40] bg-[#178A6B]/10 hover:bg-[#178A6B]/20 px-3 py-2 rounded-xl transition-all"
              >
                <Smartphone className="w-4 h-4" />
                Ver app cliente
              </a>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm">
                <Bot className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">IA</span>
                <Switch
                  checked={client.ai_active}
                  onCheckedChange={handleToggleAI}
                />
              </div>
            </div>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-white border border-gray-100 rounded-xl p-1 shadow-sm">
              <TabsTrigger value="chat" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <MessageSquare className="w-4 h-4 mr-2" />
                Consultas
              </TabsTrigger>
              <TabsTrigger value="progress" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <TrendingUp className="w-4 h-4 mr-2" />
                Progreso
              </TabsTrigger>
              <TabsTrigger value="plan" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <FileText className="w-4 h-4 mr-2" />
                Plan
              </TabsTrigger>
              <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-[#0F4D40] data-[state=active]:text-white">
                <User className="w-4 h-4 mr-2" />
                Perfil
              </TabsTrigger>
            </TabsList>

            {/* Chat/Consultas Tab - Shows Q&A summary from WhatsApp */}
            <TabsContent value="chat" className="mt-4">
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#0F4D40]/5 to-transparent">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-[#0F4D40] flex items-center gap-2">
                        <HelpCircle className="w-5 h-5" />
                        Consultas de WhatsApp
                      </h3>
                      <p className="text-sm text-gray-500">Dudas resueltas por HabitIA vía WhatsApp</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#178A6B]">{qaData.length}</p>
                      <p className="text-xs text-gray-500">consultas resueltas</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                  {qaData.map((qa, index) => (
                    <motion.div
                      key={qa.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getCategoryColor(qa.category)}`}>
                          {qa.category}
                        </span>
                        <span className="text-xs text-gray-400">{qa.date}</span>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-gray-500" />
                          </div>
                          <div className="flex-1 bg-white rounded-xl p-3 shadow-sm">
                            <p className="text-sm text-gray-700">{qa.question}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3 ml-4">
                          <div className="w-8 h-8 bg-[#0F4D40] rounded-full flex items-center justify-center flex-shrink-0">
                            <Lightbulb className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 bg-[#178A6B]/10 rounded-xl p-3 border border-[#178A6B]/20">
                            <p className="text-xs text-[#178A6B] font-medium mb-1">Respuesta de HabitIA</p>
                            <p className="text-sm text-gray-700">{qa.answer}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {qaData.length === 0 && (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No hay consultas registradas aún</p>
                      <p className="text-sm text-gray-400">Las consultas del cliente vía WhatsApp aparecerán aquí</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Progress Tab */}
            <TabsContent value="progress" className="mt-4">
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 space-y-6">
                {/* Streak */}
                <div className="text-center">
                  <div className="inline-flex items-center gap-4 bg-gradient-to-r from-[#E57A4F]/10 to-[#F59E0B]/10 px-8 py-5 rounded-2xl border border-[#E57A4F]/20">
                    <Flame className="w-12 h-12 text-[#E57A4F]" />
                    <div className="text-left">
                      <p className="text-5xl font-bold text-[#0F4D40]">{adherence?.current_streak || 0}</p>
                      <p className="text-gray-500">días consecutivos</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-[#0F4D40]">{adherence?.total_checkins || 0}</p>
                    <p className="text-sm text-gray-500">Total check-ins</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center justify-center gap-1">
                      <Star className="w-5 h-5 text-[#F59E0B] fill-current" />
                      <p className="text-2xl font-bold text-[#0F4D40]">{adherence?.avg_rating || 0}</p>
                    </div>
                    <p className="text-sm text-gray-500">Promedio</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-[#0F4D40]">{adherence?.best_streak || 0}</p>
                    <p className="text-sm text-gray-500">Mejor racha</p>
                  </div>
                  <div className="text-center p-4 bg-[#178A6B]/10 rounded-xl">
                    <p className="text-2xl font-bold text-[#178A6B]">{Math.round(adherence?.last_14_days_adherence || 0)}%</p>
                    <p className="text-sm text-gray-500">Adherencia 14d</p>
                  </div>
                </div>

                {/* Heatmap */}
                <div>
                  <h3 className="font-semibold text-[#0F4D40] mb-4">Últimos 30 días</h3>
                  <div className="flex flex-wrap gap-1">
                    {adherence?.heatmap?.map((day, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className={`w-4 h-4 rounded-sm cursor-pointer transition-transform hover:scale-125 ${
                          day.completed ? 'bg-[#178A6B]' : 'bg-gray-200'
                        }`}
                        title={`${day.date}: ${day.completed ? 'Check-in realizado' : 'Sin check-in'}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-200 rounded-sm"></div>
                      <span>Sin check-in</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-[#178A6B] rounded-sm"></div>
                      <span>Check-in realizado</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Plan Tab */}
            <TabsContent value="plan" className="mt-4">
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                {/* Upload Section */}
                <div className="mb-6">
                  <h3 className="font-semibold text-[#0F4D40] mb-3 flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Subir Plan de Dieta
                  </h3>
                  
                  <div 
                    onClick={() => !isUploading && fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                      isUploading 
                        ? 'border-[#178A6B] bg-[#178A6B]/5' 
                        : 'border-gray-200 hover:border-[#178A6B] hover:bg-[#178A6B]/5'
                    }`}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    
                    {isUploading ? (
                      <div className="space-y-3">
                        <div className="w-12 h-12 border-4 border-[#178A6B] border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="text-[#178A6B] font-medium">Procesando documento...</p>
                        <div className="w-48 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
                          <div 
                            className="h-full bg-[#178A6B] transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500">La IA está extrayendo el plan de dieta</p>
                      </div>
                    ) : (
                      <>
                        <File className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600 font-medium mb-1">
                          Arrastra o haz clic para subir
                        </p>
                        <p className="text-sm text-gray-400">
                          PDF, Word o imagen • La IA extraerá el plan automáticamente
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Plans List */}
                {plans.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No hay planes de dieta</p>
                    <p className="text-sm text-gray-400">Sube un documento para crear el primer plan</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-[#0F4D40]">Planes de dieta</h3>
                    
                    {plans.map((plan) => (
                      <motion.div 
                        key={plan.plan_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-5 rounded-xl border-2 transition-all ${
                          plan.is_active 
                            ? 'border-[#178A6B] bg-[#178A6B]/5' 
                            : 'border-gray-100 hover:border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-[#0F4D40]">{plan.name}</h4>
                            {plan.is_active && (
                              <span className="flex items-center gap-1 text-xs bg-[#178A6B] text-white px-2.5 py-1 rounded-full">
                                <CheckCircle className="w-3 h-3" />
                                Activo
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(plan.created_at).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
                        
                        {plan.foods_extracted?.length > 0 && (
                          <div className="grid grid-cols-2 gap-3">
                            {plan.foods_extracted.map((meal, i) => (
                              <div key={i} className="bg-white p-3 rounded-lg border border-gray-100">
                                <div className="flex justify-between items-center mb-1">
                                  <p className="font-medium text-gray-700 text-sm">{meal.meal}</p>
                                  <span className="text-xs text-[#178A6B] font-medium">{meal.calories} kcal</span>
                                </div>
                                <p className="text-xs text-gray-500">{meal.foods?.join(', ')}</p>
                                <div className="flex gap-2 mt-2 text-xs">
                                  <span className="text-red-500">P: {meal.protein}g</span>
                                  <span className="text-amber-500">C: {meal.carbs}g</span>
                                  <span className="text-blue-500">G: {meal.fat}g</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                          <span className="text-sm text-gray-500">Total diario</span>
                          <span className="text-lg font-bold text-[#0F4D40]">
                            {plan.foods_extracted?.reduce((sum, m) => sum + m.calories, 0) || 0} kcal
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" className="mt-4">
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-gray-500">Nombre</Label>
                    <p className="font-medium text-[#0F4D40] text-lg">{client.name}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Teléfono</Label>
                    <p className="font-medium text-[#0F4D40]">{client.phone}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium text-[#0F4D40]">{client.email || "No especificado"}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Hora de recordatorio</Label>
                    {editingProfile ? (
                      <Input
                        type="time"
                        value={profileData.reminder_time}
                        onChange={(e) => setProfileData({ ...profileData, reminder_time: e.target.value })}
                        className="mt-1"
                      />
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <p className="font-medium text-[#0F4D40]">{client.reminder_time}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-gray-500">Objetivo</Label>
                  {editingProfile ? (
                    <Input
                      value={profileData.goal}
                      onChange={(e) => setProfileData({ ...profileData, goal: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <div className="flex items-start gap-2 mt-1">
                      <Target className="w-4 h-4 text-gray-400 mt-0.5" />
                      <p className="font-medium text-[#0F4D40]">{client.goal}</p>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-gray-500">Notas privadas</Label>
                  {editingProfile ? (
                    <Textarea
                      value={profileData.notes}
                      onChange={(e) => setProfileData({ ...profileData, notes: e.target.value })}
                      placeholder="Agrega notas sobre este cliente..."
                      className="mt-1"
                      rows={3}
                    />
                  ) : (
                    <p className="text-gray-700 mt-1 bg-gray-50 p-3 rounded-lg">
                      {client.notes || "Sin notas"}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-gray-500">Pregunta de check-in</Label>
                  {editingProfile ? (
                    <Input
                      value={profileData.checkin_question}
                      onChange={(e) => setProfileData({ ...profileData, checkin_question: e.target.value })}
                      className="mt-1"
                    />
                  ) : (
                    <p className="font-medium text-[#0F4D40] mt-1">{client.checkin_question}</p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  {editingProfile ? (
                    <div className="flex gap-3">
                      <Button
                        onClick={handleSaveProfile}
                        className="bg-[#178A6B] hover:bg-[#0F4D40]"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Guardar cambios
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEditingProfile(false)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setEditingProfile(true)}
                    >
                      Editar perfil
                    </Button>
                  )}
                  
                  <Button
                    variant="destructive"
                    onClick={handleDeleteClient}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Cliente
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default ClientDetail;
